/** @jsxImportSource @emotion/react */
import { useMemo, useState, useEffect } from "react";
import { graphql, useLazyLoadQuery } from "react-relay";
import { css } from "@emotion/react";
import {
    Area,
    AreaChart,
    CartesianGrid,
    Legend,
    ResponsiveContainer,
    Tooltip,
    TooltipContentProps,
    XAxis,
    YAxis,
} from "recharts";
import {
    ChartTooltip,
    ChartTooltipItem,
    defaultCartesianGridProps,
    defaultLegendProps,
    defaultXAxisProps,
    defaultYAxisProps,
    useBinInterval,
} from "@phoenix/components/chart";
import { useTimeBinScale } from "@phoenix/hooks/useTimeBin";
import { useUTCOffsetMinutes } from "@phoenix/hooks/useUTCOffsetMinutes";
import { ProjectMetricViewProps } from "@phoenix/pages/project/metrics/types";
import { costFormatter, intShortFormatter } from "@phoenix/utils/numberFormatUtils";
import type { ModelUsageSummaryQuery } from "./__generated__/ModelUsageSummaryQuery.graphql";

const containerCSS = css`
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 8px;
`;

const widgetHeaderCSS = css`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const selectCSS = css`
  background: var(--ac-global-color-grey-100);
  border: 1px solid var(--ac-global-color-grey-300);
  border-radius: 8px;
  color: var(--ac-global-text-color-900);
  font-size: 13px;
  padding: 6px 12px;
  cursor: pointer;
  outline: none;
  min-width: 150px;
  &:hover {
    border-color: var(--ac-global-color-primary);
  }
`;

const tabsHeaderCSS = css`
  display: flex;
  gap: 20px;
  border-bottom: 1px solid var(--ac-global-color-grey-200);
  margin-bottom: 16px;
  padding: 0;
`;

const tabBtnCSS = (active: boolean) => css`
  padding: 8px 0;
  border: none;
  background: none;
  font-size: 13px;
  font-weight: 500;
  color: ${active ? "var(--ac-global-color-primary)" : "var(--ac-global-text-color-500)"};
  border-bottom: 2px solid ${active ? "var(--ac-global-color-primary)" : "transparent"};
  cursor: pointer;
  transition: all 0.2s;
  &:hover {
    color: var(--ac-global-color-primary);
  }
`;

const chartHeaderCSS = css`
  margin-bottom: 16px;
`;

const bigNumberCSS = css`
  font-size: 32px;
  font-weight: 700;
  color: var(--ac-global-text-color-900);
  margin-right: 8px;
`;

const subtitleCSS = css`
  font-size: 14px;
  color: var(--ac-global-text-color-500);
`;

const chartContainerCSS = css`
  flex: 1;
  min-height: 250px;
`;

const TAB_COST_MODEL = "COST_MODEL";
const TAB_COST_TYPE = "COST_TYPE";
const TAB_USAGE_MODEL = "USAGE_MODEL";
const TAB_USAGE_TYPE = "USAGE_TYPE";

const COLOR_PALETTE = ["#4338ca", "#0e7490", "#eab308", "#6d28d9", "#047857", "#b45309", "#be185d", "#4d7c0f", "#0369a1", "#7e22ce"];

function CustomTooltip({
    active,
    payload,
    label,
    valueFormatter,
    scale,
}: TooltipContentProps<number, string> & { valueFormatter: (v: number) => string; scale?: string }) {
    if (active && payload && payload.length) {
        const date = new Date(label || "");
        const isDayScale = scale === "DAY";
        const timeStr = date.toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            ...(isDayScale ? {} : { hour: '2-digit', minute: '2-digit', hour12: true })
        }).replace(',', '');

        return (
            <ChartTooltip>
                <div style={{ marginBottom: 4, fontWeight: 700, fontSize: 12 }}>{timeStr}</div>
                {payload.map((p, i) => (
                    <ChartTooltipItem
                        key={i}
                        color={p.color || "#6366f1"}
                        shape="circle"
                        name={p.name || ""}
                        value={valueFormatter(p.value as number)}
                    />
                ))}
            </ChartTooltip>
        );
    }
    return null;
}

export function ModelUsageSummary({ projectId, timeRange, fetchKey, widgetId, widgetTitle, onDataExport }: ProjectMetricViewProps) {
    const [activeTab, setActiveTab] = useState(TAB_COST_MODEL);
    const [selectedModel, setSelectedModel] = useState("all");

    const defaultScale = useTimeBinScale({ timeRange });
    const utcOffsetMinutes = useUTCOffsetMinutes();

    // Machi, respect the aggregation! Week/Month/Day (5-35 days) should stay as DAY for cleaner views
    const scale = useMemo(() => {
        if (defaultScale === "MONTH" || defaultScale === "WEEK" || defaultScale === "DAY") return "DAY";
        return defaultScale;
    }, [defaultScale]);

    const data = useLazyLoadQuery<ModelUsageSummaryQuery>(
        graphql`
      query ModelUsageSummaryQuery($projectId: ID!, $timeRange: TimeRange!, $timeBinConfig: TimeBinConfig!, $filterCondition: String) {
        project: node(id: $projectId) {
          ... on Project {
            topModelsByCost(timeRange: $timeRange) {
              name
              costSummary(projectId: $projectId, timeRange: $timeRange) {
                total { cost tokens }
                prompt { cost tokens }
                completion { cost tokens }
              }
            }
            spanCountTimeSeries(timeRange: $timeRange, timeBinConfig: $timeBinConfig, filterCondition: $filterCondition) {
              data { timestamp okCount errorCount unsetCount }
            }
            # Fetch spans for breakdown visualization - increased for large time ranges machi!
            spans(timeRange: $timeRange, first: 5000) {
              edges {
                node {
                  name
                  modelName
                  startTime
                  tokenCountPrompt
                  tokenCountCompletion
                  costSummary { total { cost } }
                }
              }
            }
          }
        }
      }
    `,
        {
            projectId,
            timeRange: { start: timeRange.start?.toISOString(), end: timeRange.end?.toISOString() },
            timeBinConfig: { scale, utcOffsetMinutes },
            filterCondition: "", // Always fetch project trend for reliable visualization
        },
        { fetchPolicy: "network-only", fetchKey }
    );

    const models = data.project?.topModelsByCost ?? [];

    const { chartData, totalValue } = useMemo(() => {
        let total = 0;
        let chart: any[] = [];

        const raw = data.project?.spanCountTimeSeries?.data ?? [];
        const edges = data.project?.spans?.edges ?? [];

        // Build bin dateKey using local timezone (UTC + utcOffsetMinutes)
        const binKeys = raw.map((d: any) => {
            const localMs = new Date(d.timestamp).getTime() + utcOffsetMinutes * 60 * 1000;
            return new Date(localMs).toISOString().slice(0, 10);
        });

        // Build per-day per-model map using ACTUAL modelName from each span
        // This is the CORRECT approach: kimi cost only lands on days kimi actually ran
        const dayModelMap: Record<string, Record<string, { cost: number; tokens: number }>> = {};

        // Build normalization map: span model_name (e.g. 'kimi-k2.5') -> topModelsByCost name (e.g. 'moonshot/kimi-k2.5')
        // span.model_name is the raw extracted model name, topModelsByCost.name is the full canonical name from generative_models table
        const modelNameNormMap: Record<string, string> = {};
        models.forEach((m: any) => {
            const fullName = m.name as string;
            // Exact match
            modelNameNormMap[fullName] = fullName;
            // Suffix match: 'moonshot/kimi-k2.5' -> also maps 'kimi-k2.5' to 'moonshot/kimi-k2.5'
            const parts = fullName.split("/");
            if (parts.length > 1) {
                const shortName = parts[parts.length - 1];
                if (!modelNameNormMap[shortName]) {
                    modelNameNormMap[shortName] = fullName;
                }
            }
        });

        edges.forEach((edge: any) => {
            const node = edge?.node;
            if (!node) return;
            // Skip wrapper spans (altergate_request, proxy_request) to avoid double counting tokens/cost:
            // GraphQL fallback returns cumulative tokens on parent wrapper span when own tokens are 0,
            // which causes parent (74) + child (74) = 148 in chart tooltip.
            if (node.name === "altergate_request" || node.name === "proxy_request") return;

            const spanLocalMs = new Date(node.startTime).getTime() + utcOffsetMinutes * 60 * 1000;
            const spanDate = new Date(spanLocalMs).toISOString().slice(0, 10);
            // Normalize span model_name to full canonical name (e.g. 'kimi-k2.5' -> 'moonshot/kimi-k2.5')
            const rawModelKey = node.modelName || "_unknown";
            const modelKey = modelNameNormMap[rawModelKey] || rawModelKey;
            if (!dayModelMap[spanDate]) dayModelMap[spanDate] = {};
            if (!dayModelMap[spanDate][modelKey]) dayModelMap[spanDate][modelKey] = { cost: 0, tokens: 0 };
            // Use costSummary.total.cost for accurate per-span cost
            const spanCost = node.costSummary?.total?.cost ?? 0;
            dayModelMap[spanDate][modelKey].cost += spanCost;
            dayModelMap[spanDate][modelKey].tokens += (node.tokenCountPrompt ?? 0) + (node.tokenCountCompletion ?? 0);
        });

        if (activeTab === TAB_COST_MODEL || activeTab === TAB_COST_TYPE) {
            let promptTotal = 0, completionTotal = 0;
            if (selectedModel === "all") {
                promptTotal = models.reduce((acc: number, m: any) => acc + (m.costSummary?.prompt?.cost ?? 0), 0);
                completionTotal = models.reduce((acc: number, m: any) => acc + (m.costSummary?.completion?.cost ?? 0), 0);
                total = promptTotal + completionTotal;
            } else {
                const modelObj = models.find((m: any) => m.name === selectedModel);
                promptTotal = modelObj?.costSummary?.prompt?.cost ?? 0;
                completionTotal = modelObj?.costSummary?.completion?.cost ?? 0;
                total = modelObj?.costSummary?.total?.cost ?? 0;
            }
            const promptRatio = promptTotal / (total || 1);
            const completionRatio = completionTotal / (total || 1);

            chart = raw.map((d: any, idx: number) => {
                const dateKey = binKeys[idx];
                const dayModels = dayModelMap[dateKey] ?? {};
                let dayCost = 0;
                if (selectedModel === "all") {
                    // Sum all models' actual cost for this day
                    dayCost = Object.values(dayModels).reduce((s, v) => s + v.cost, 0);
                } else {
                    // ONLY kimi's actual cost on this day (zero if kimi didn't run)
                    dayCost = dayModels[selectedModel]?.cost ?? 0;
                }
                const point: any = {
                    timestamp: d.timestamp,
                    total: dayCost,
                    prompt: dayCost * promptRatio,
                    completion: dayCost * completionRatio,
                    value: dayCost,
                    _activity: dayCost > 0 ? 1 : 0,
                };
                if (selectedModel === "all" && activeTab === TAB_COST_MODEL) {
                    models.forEach((m: any) => {
                        point[m.name] = dayModels[m.name]?.cost ?? 0;
                    });
                }
                return point;
            });
        } else {
            let promptTotal = 0, completionTotal = 0;
            if (selectedModel === "all") {
                promptTotal = models.reduce((acc: number, m: any) => acc + (m.costSummary?.prompt?.tokens ?? 0), 0);
                completionTotal = models.reduce((acc: number, m: any) => acc + (m.costSummary?.completion?.tokens ?? 0), 0);
                total = promptTotal + completionTotal;
            } else {
                const modelObj = models.find((m: any) => m.name === selectedModel);
                promptTotal = modelObj?.costSummary?.prompt?.tokens ?? 0;
                completionTotal = modelObj?.costSummary?.completion?.tokens ?? 0;
                total = modelObj?.costSummary?.total?.tokens ?? 0;
            }
            const promptRatio = promptTotal / (total || 1);
            const completionRatio = completionTotal / (total || 1);

            chart = raw.map((d: any, idx: number) => {
                const dateKey = binKeys[idx];
                const dayModels = dayModelMap[dateKey] ?? {};
                let dayTokens = 0;
                if (selectedModel === "all") {
                    dayTokens = Object.values(dayModels).reduce((s, v) => s + v.tokens, 0);
                } else {
                    dayTokens = dayModels[selectedModel]?.tokens ?? 0;
                }
                const point: any = {
                    timestamp: d.timestamp,
                    total: dayTokens,
                    prompt: dayTokens * promptRatio,
                    completion: dayTokens * completionRatio,
                    value: dayTokens,
                    _activity: dayTokens > 0 ? 1 : 0,
                };
                if (selectedModel === "all" && activeTab === TAB_USAGE_MODEL) {
                    models.forEach((m: any) => {
                        point[m.name] = dayModels[m.name]?.tokens ?? 0;
                    });
                }
                return point;
            });
        }

        // Auto-Zoom: crop to active data range with small buffer
        const firstActiveIdx = chart.findIndex((d: any) => d._activity > 0);
        const lastActiveIdx = chart.length - [...chart].reverse().findIndex((d: any) => d._activity > 0);
        const cropped = (firstActiveIdx === -1) ? chart : chart.slice(
            Math.max(0, firstActiveIdx - 3),
            Math.min(chart.length, lastActiveIdx + 3)
        );

        return { chartData: cropped, totalValue: total };
    }, [data, activeTab, models, selectedModel, utcOffsetMinutes]);

    useEffect(() => {
        if (chartData.length > 0 && onDataExport && widgetId && widgetTitle) {
            onDataExport(widgetId, widgetTitle, chartData);
        }
    }, [chartData, onDataExport, widgetId, widgetTitle]);

    const timeTickFormatter = (value: string) => {
        const date = new Date(value);
        if (scale === "DAY") {
            return date.toLocaleString('en-US', { month: 'numeric', day: 'numeric' });
        }
        return date.toLocaleString('en-US', { month: 'numeric', day: 'numeric', hour: '2-digit', hour12: true });
    };

    const interval = useBinInterval({ scale });
    const fmt = (v: number) => (activeTab.includes("COST") ? costFormatter(v) : intShortFormatter(v));

    return (
        <div css={containerCSS}>
            <div css={widgetHeaderCSS}>
                <div css={tabsHeaderCSS}>
                    <button css={tabBtnCSS(activeTab === TAB_COST_MODEL)} onClick={() => setActiveTab(TAB_COST_MODEL)}>Cost by model</button>
                    <button css={tabBtnCSS(activeTab === TAB_COST_TYPE)} onClick={() => setActiveTab(TAB_COST_TYPE)}>Cost by type</button>
                    <button css={tabBtnCSS(activeTab === TAB_USAGE_MODEL)} onClick={() => setActiveTab(TAB_USAGE_MODEL)}>Usage by model</button>
                    <button css={tabBtnCSS(activeTab === TAB_USAGE_TYPE)} onClick={() => setActiveTab(TAB_USAGE_TYPE)}>Usage by type</button>
                </div>
                <select css={selectCSS} value={selectedModel} onChange={(e) => setSelectedModel(e.target.value)}>
                    <option value="all">{models.length > 0 ? `${models.length} model${models.length === 1 ? "" : "s"}` : "All models"}</option>
                    {models.map(m => <option key={m.name} value={m.name}>{m.name}</option>
                    )}
                </select>
            </div>

            <div css={chartHeaderCSS}>
                <span css={bigNumberCSS}>{fmt(totalValue)}</span>
                <span css={subtitleCSS}>
                    {activeTab.includes("COST") ? "Cost" : (selectedModel === "all" ? "Units (Tokens)" : "Units (Calls)")}
                </span>
            </div>

            <div css={chartContainerCSS}>
                <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 5, right: 30, left: 10, bottom: 20 }}>
                        <defs>
                            <linearGradient id="colorMain" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#4338ca" stopOpacity={0.85} />
                                <stop offset="95%" stopColor="#4338ca" stopOpacity={0.15} />
                            </linearGradient>
                            <linearGradient id="colorPrompt" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#0e7490" stopOpacity={0.7} />
                                <stop offset="95%" stopColor="#0e7490" stopOpacity={0.15} />
                            </linearGradient>
                            <linearGradient id="colorTotal" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#b45309" stopOpacity={0.4} />
                                <stop offset="95%" stopColor="#b45309" stopOpacity={0.05} />
                            </linearGradient>
                            {/* Machi, deeper/richer gradients for models! */}
                            {COLOR_PALETTE.map((color, i) => (
                                <linearGradient key={`grad-${i}`} id={`grad-${i}`} x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor={color} stopOpacity={0.8} />
                                    <stop offset="95%" stopColor={color} stopOpacity={0.15} />
                                </linearGradient>
                            ))}
                        </defs>
                        <CartesianGrid {...defaultCartesianGridProps} vertical={false} strokeDasharray="3 3" opacity={0.3} />
                        <XAxis
                            {...defaultXAxisProps}
                            dataKey="timestamp"
                            interval="preserveStartEnd"
                            minTickGap={80}
                            tickFormatter={timeTickFormatter}
                            tick={{ fontSize: 10, fill: "var(--ac-global-text-color-500)" }}
                        />
                        <YAxis
                            {...defaultYAxisProps}
                            width={50}
                            tickFormatter={fmt}
                            tick={{ fontSize: 10 }}
                        />
                        <Tooltip content={(props) => <CustomTooltip {...props} valueFormatter={fmt} scale={scale} />} />

                        {(activeTab === TAB_COST_TYPE || activeTab === TAB_USAGE_TYPE) && (
                            <>
                                <Area
                                    type="monotone"
                                    dataKey="prompt"
                                    name="Input"
                                    stroke="#0e7490"
                                    strokeWidth={2.5}
                                    fill="url(#colorPrompt)"
                                    dot={{ r: 3, fill: "#eab308", stroke: "#fff", strokeWidth: 1.5 }}
                                    activeDot={{ r: 6, stroke: "#fff", strokeWidth: 2 }}
                                />
                                <Area
                                    type="monotone" dataKey="completion" name="Output" stroke="#4338ca" strokeWidth={2.5} fill="url(#colorMain)"
                                    dot={{ r: 3, fill: "#eab308", stroke: "#fff", strokeWidth: 1.5 }}
                                    activeDot={{ r: 6, stroke: "#fff", strokeWidth: 2 }}
                                />
                                <Area
                                    type="monotone"
                                    dataKey="total"
                                    name="Total"
                                    stroke="#b45309"
                                    strokeWidth={3}
                                    fill="url(#colorTotal)"
                                    dot={{ r: 4, fill: "#eab308", stroke: "#fff", strokeWidth: 2 }}
                                    activeDot={{ r: 7, stroke: "#fff", strokeWidth: 2.5 }}
                                    strokeDasharray="0"
                                />
                            </>
                        )}
                        {(activeTab === TAB_COST_MODEL || activeTab === TAB_USAGE_MODEL) && selectedModel === "all" && (
                            models.map((m: any, idx: number) => {
                                const color = COLOR_PALETTE[idx % COLOR_PALETTE.length];
                                return (
                                    <Area
                                        key={m.name}
                                        type="monotone"
                                        dataKey={m.name}
                                        name={m.name}
                                        stroke={color}
                                        strokeWidth={3}
                                        fill={`url(#grad-${idx % COLOR_PALETTE.length})`}
                                        dot={{ r: 3, fill: "#eab308", stroke: "#fff", strokeWidth: 1.5 }}
                                        activeDot={{ r: 6, stroke: "#fff", strokeWidth: 2 }}
                                    />
                                );
                            })
                        )}
                        {(activeTab === TAB_COST_MODEL || activeTab === TAB_USAGE_MODEL) && selectedModel !== "all" && (
                            <Area
                                type="monotone"
                                dataKey="value"
                                name={selectedModel}
                                stroke="#4338ca"
                                strokeWidth={3}
                                fill="url(#colorMain)"
                                dot={{ r: 4, fill: "#eab308", stroke: "#fff", strokeWidth: 2 }}
                                activeDot={{ r: 7, stroke: "#fff", strokeWidth: 2.5 }}
                                animationDuration={1000}
                            />
                        )}
                        <Legend {...defaultLegendProps} iconType="circle" />
                    </AreaChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
}
